import React, { useState } from 'react';
import { 
  Shield, ShieldCheck, Info,
  Layers, GitBranch, ChevronDown, ChevronUp, Settings2, HatGlasses 
} from 'lucide-react';

/**
 * DeepScanForm - Formulaire de configuration de deep scan réutilisable
 * 
 * @param {object} config - Configuration actuelle { verifiedOnly, maxDepth, branch, deepRecovery }
 * @param {function} onChange - Callback appelé quand la config change
 * @param {boolean} disabled - Désactiver le formulaire (ex: scan en cours)
 * @param {boolean} showAdvancedByDefault - Afficher les options avancées par défaut
 * @param {string} className - Classes CSS additionnelles
 */
const DeepScanForm = ({
  config = {},
  onChange,
  disabled = false,
  showAdvancedByDefault = false,
  className = ''
}) => {
  const {
    verifiedOnly = true,
    maxDepth = '',
    branch = '',
    deepRecovery = false
  } = config;

  const [showAdvanced, setShowAdvanced] = useState(showAdvancedByDefault);

  const handleChange = (key, value) => {
    onChange?.({ ...config, [key]: value });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Scan Mode Selection */}
      <div>
        <label className="text-caption font-medium text-ink mb-2 block">Scan Mode</label>
        
        {/* Radio Cards */}
        <div className="grid grid-cols-2 gap-3">
          {/* Verified Only Card */}
          <button
            type="button"
            onClick={() => !disabled && handleChange('verifiedOnly', true)}
            disabled={disabled}
            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
              verifiedOnly
                ? 'border-accent bg-accent/5'
                : 'border-border bg-surface-tertiary hover:border-ink-muted'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {/* Selection indicator */}
            <div className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              verifiedOnly ? 'border-accent bg-accent' : 'border-ink-muted'
            }`}>
              {verifiedOnly && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
            </div>
            
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg flex-shrink-0 ${verifiedOnly ? 'bg-accent/20' : 'bg-surface-secondary'}`}>
                <ShieldCheck size={18} className={verifiedOnly ? 'text-accent' : 'text-ink-secondary'} />
              </div>
              <div className="pr-4">
                <div className={`text-caption font-medium ${verifiedOnly ? 'text-ink' : 'text-ink-secondary'}`}>
                  Verified Only
                </div>
                <div className="text-tiny text-ink-tertiary mt-1">
                  Only secrets verified as currently active
                </div>
              </div>
            </div>
          </button>

          {/* All Secrets Card */}
          <button
            type="button"
            onClick={() => !disabled && handleChange('verifiedOnly', false)}
            disabled={disabled}
            className={`relative p-4 rounded-lg border-2 text-left transition-all ${
              !verifiedOnly
                ? 'border-orange-500 bg-orange-500/5'
                : 'border-border bg-surface-tertiary hover:border-ink-muted'
            } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {/* Selection indicator */}
            <div className={`absolute top-3 right-3 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
              !verifiedOnly ? 'border-orange-500 bg-orange-500' : 'border-ink-muted'
            }`}>
              {!verifiedOnly && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
            </div>
            
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg flex-shrink-0 ${!verifiedOnly ? 'bg-orange-500/20' : 'bg-surface-secondary'}`}>
                <Shield size={18} className={!verifiedOnly ? 'text-orange-400' : 'text-ink-secondary'} />
              </div>
              <div className="pr-4">
                <div className={`text-caption font-medium ${!verifiedOnly ? 'text-ink' : 'text-ink-secondary'}`}>
                  All Secrets
                </div>
                <div className="text-tiny text-ink-tertiary mt-1">
                  All detected secrets including unverified
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Advanced Options Toggle */}
      <button
        type="button"
        onClick={() => setShowAdvanced(!showAdvanced)}
        disabled={disabled}
        className="flex items-center gap-2 text-caption text-ink-secondary hover:text-ink transition-colors disabled:opacity-50"
      >
        <Settings2 size={14} />
        <span>Advanced Options</span>
        {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {/* Advanced Options Panel */}
      {showAdvanced && (
        <div className="p-4 bg-surface-tertiary rounded-lg border border-border space-y-4">
          {/* Max Depth */}
          <div className={`flex items-center gap-3 ${deepRecovery ? 'opacity-50' : ''}`}>
            <div className="p-2 rounded-lg bg-surface-secondary">
              <Layers size={16} className="text-ink-secondary" />
            </div>
            <div className="flex-1">
              <label className="text-caption font-medium text-ink block mb-1">
                Commit Depth
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="1"
                  placeholder="Unlimited"
                  value={maxDepth}
                  onChange={(e) => handleChange('maxDepth', e.target.value)}
                  disabled={disabled || deepRecovery}
                  className="w-32 px-3 py-1.5 text-caption bg-surface border border-border rounded-lg text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent disabled:opacity-50"
                />
                <span className="text-tiny text-ink-tertiary">
                  commits (empty = all)
                </span>
              </div>
            </div>
          </div>

          {/* Branch */}
          <div className={`flex items-center gap-3 ${deepRecovery ? 'opacity-50' : ''}`}>
            <div className="p-2 rounded-lg bg-surface-secondary">
              <GitBranch size={16} className="text-ink-secondary" />
            </div>
            <div className="flex-1">
              <label className="text-caption font-medium text-ink block mb-1">
                Branch
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  placeholder="All branches"
                  value={branch}
                  onChange={(e) => handleChange('branch', e.target.value)}
                  disabled={disabled || deepRecovery}
                  className="w-48 px-3 py-1.5 text-caption bg-surface border border-border rounded-lg text-ink placeholder:text-ink-tertiary focus:outline-none focus:ring-2 focus:ring-accent/20 focus:border-accent disabled:opacity-50"
                />
                <span className="text-tiny text-ink-tertiary">
                  e.g. main, develop
                </span>
              </div>
            </div>
          </div>

          {/* Separator */}
          <div className="border-t border-border" />

          {/* Deep Recovery Mode */}
          <div className="flex items-center justify-between p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-500/20">
                <HatGlasses size={16} className="text-red-400" />
              </div>
              <div>
                <div className="text-caption font-medium text-ink flex items-center gap-2">
                  Deep Recovery Mode
                  <span className="text-tiny px-1.5 py-0.5 bg-red-500/20 text-red-400 rounded">BETA</span>
                </div>
                <div className="text-tiny text-ink-tertiary">
                  Find secrets in dangling blobs & deleted files
                </div>
              </div>
            </div>
            <button
              type="button"
              onClick={() => !disabled && handleChange('deepRecovery', !deepRecovery)}
              disabled={disabled}
              className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${
                deepRecovery ? 'bg-red-500' : 'bg-ink-muted'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all shadow-sm ${
                  deepRecovery ? 'left-7' : 'left-1'
                }`}
              />
            </button>
          </div>

          {deepRecovery && (
            <div className="flex items-start gap-2 text-tiny text-red-400 bg-red-500/10 p-2 rounded-lg">
              <Shield size={14} className="flex-shrink-0 mt-0.5" />
              <span>Deep Recovery clones the entire repo, extracts dangling blobs and deleted files. This can take much longer but finds secrets that were amended/rebased away.</span>
            </div>
          )}

          {/* Info text */}
          {!deepRecovery && (
            <div className="flex items-center gap-2 text-tiny text-ink-tertiary">
              <Info size={14} className="flex-shrink-0" />
              <span>Leave empty for maximum exhaustiveness (scans all commits on all branches)</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DeepScanForm;
